import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  CalendarClock,
  CheckCircle2,
  HeartHandshake,
  MessageSquareMore,
  ShieldAlert,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Page = styled.main`
  min-height: 100vh;
  overflow-x: hidden;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(920px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 118px 20px 90px;
`;

const Highlight = styled.span`
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Hero = styled.section`
  padding: 34px;
  border-radius: 34px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.86), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.85fr);
  gap: 24px;
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Title = styled(motion.h1)`
  margin: 18px 0 0;
  font-size: clamp(40px, 5vw, 62px);
  line-height: 1.04;
  font-weight: 1000;
  color: #f8fafc;
  max-width: 760px;
`;

const Sub = styled(motion.p)`
  margin-top: 16px;
  max-width: 780px;
  font-size: 18px;
  line-height: 1.85;
  color: #94a3b8;
`;

const CTAWrap = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PrimaryCTA = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 900;
  color: #04111f;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SecondaryCTA = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  cursor: pointer;
  font-size: 14px;
  font-weight: 900;
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ProofPanel = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const ProofLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ProofTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 1000;
`;

const ProofList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const ProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const Section = styled.section`
  margin-top: 34px;
  padding: 28px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 30px;
  font-weight: 1000;
`;

const SectionText = styled.p`
  margin-top: 12px;
  max-width: 860px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const WhyCard = styled(motion.div)`
  margin-top: 18px;
  padding: 24px;
  border-radius: 24px;
  background: rgba(2, 6, 23, 0.38);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  line-height: 1.85;
  font-size: 16px;
`;

const Grid = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  padding: 22px;
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.6)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 18px 50px rgba(2, 6, 23, 0.26);
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  margin-bottom: 18px;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  color: #04111f;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 22px;
  font-weight: 900;
`;

const CardText = styled.p`
  margin-top: 12px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const NoteSection = styled.section`
  margin-top: 34px;
  padding: 32px;
  border-radius: 30px;
  text-align: center;
  background:
    radial-gradient(800px at 50% 0%, rgba(245, 158, 11, 0.16), transparent 60%),
    rgba(15, 23, 42, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 900;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.18);
  color: #fbbf24;
`;

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

export default function CommunityPage() {
  const navigate = useNavigate();

  return (
    <Page>
      <Container>
        <Hero>
          <HeroGrid>
            <div>
              <Eyebrow>
                <Users size={14} />
                Roadmap Community
              </Eyebrow>

              <Title variants={fadeUp} initial="hidden" animate="show">
                A <Highlight>developer community</Highlight> built for progress,
                not noise
              </Title>

              <Sub variants={fadeUp} initial="hidden" animate="show">
                skilll.dev community is designed for learners who want better
                direction, fewer distractions, meaningful progress updates, and
                a safer place to ask for help when they feel stuck.
              </Sub>

              <CTAWrap>
                <PrimaryCTA onClick={() => navigate("/community/feeds")}>
                  Open Community Feed
                  <ArrowRight size={16} />
                </PrimaryCTA>
                <SecondaryCTA onClick={() => navigate("/pricing")}>
                  See Paid Features
                </SecondaryCTA>
              </CTAWrap>
            </div>

            <ProofPanel>
              <ProofLabel>Why it matters</ProofLabel>
              <ProofTitle>Community should increase clarity, not confusion</ProofTitle>
              <ProofList>
                <ProofItem>
                  <ShieldAlert size={16} />
                  Fewer ego-driven conversations and more useful context
                </ProofItem>
                <ProofItem>
                  <HeartHandshake size={16} />
                  Progress, blockers, and real help instead of random noise
                </ProofItem>
                <ProofItem>
                  <Brain size={16} />
                  Better connection between roadmap, mentors, and learning momentum
                </ProofItem>
              </ProofList>
            </ProofPanel>
          </HeroGrid>
        </Hero>

        <Section>
          <SectionTitle>Why most developer communities fail</SectionTitle>
          <SectionText>
            Most communities become endless chat streams where questions get
            buried, replies are inconsistent, and beginners leave with more
            confusion than clarity. skilll.dev takes a slower, more intentional
            approach.
          </SectionText>

          <WhyCard variants={fadeUp} initial="hidden" whileInView="show">
            You ask a question. Someone replies with a strong opinion. Someone
            else says the opposite. Then the conversation shifts. That is not
            support. That is noise. This community is designed around roadmap
            progress, blockers, proof of work, and the kind of help that
            actually moves people forward.
          </WhyCard>
        </Section>

        <Section>
          <SectionTitle>What you get in the skilll.dev community</SectionTitle>
          <SectionText>
            The community is connected to the product itself, so it is not just
            another chat room. It sits closer to roadmap execution, support,
            consistency, and visible growth.
          </SectionText>

          <Grid>
            <Card variants={fadeUp} initial="hidden" whileInView="show">
              <IconWrap>
                <ShieldAlert size={26} />
              </IconWrap>
              <CardTitle>No spam, less ego, more signal</CardTitle>
              <CardText>
                Conversations are meant to support learning, progress updates,
                project sharing, and real blockers instead of flex-heavy noise.
              </CardText>
            </Card>

            <Card variants={fadeUp} initial="hidden" whileInView="show">
              <IconWrap>
                <HeartHandshake size={26} />
              </IconWrap>
              <CardTitle>People who care about progress</CardTitle>
              <CardText>
                The best help often comes from people who remember what being
                stuck feels like and can explain the next useful move clearly.
              </CardText>
            </Card>

            <Card variants={fadeUp} initial="hidden" whileInView="show">
              <IconWrap>
                <MessageSquareMore size={26} />
              </IconWrap>
              <CardTitle>Blockers and updates with context</CardTitle>
              <CardText>
                Instead of random posts, the space encourages clearer updates,
                practical blockers, roadmap questions, and career-relevant
                conversations.
              </CardText>
            </Card>

            <Card variants={fadeUp} initial="hidden" whileInView="show">
              <IconWrap>
                <CalendarClock size={26} />
              </IconWrap>
              <CardTitle>Your time is respected</CardTitle>
              <CardText>
                The goal is not to keep you scrolling. It is to help you ask,
                learn, share, and leave with better clarity than you came with.
              </CardText>
            </Card>
          </Grid>
        </Section>

        <NoteSection>
          <Badge>
            <CheckCircle2 size={16} />
            Built intentionally
          </Badge>

          <Title
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            style={{ marginTop: 22, fontSize: "clamp(34px, 4vw, 52px)" }}
          >
            The goal is not a huge community.
            <br />
            The goal is a <Highlight>useful one.</Highlight>
          </Title>

          <Sub
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            style={{ marginLeft: "auto", marginRight: "auto" }}
          >
            skilll.dev community is being shaped around helpfulness, momentum,
            and real career progress so that the environment itself supports the
            roadmap instead of distracting from it.
          </Sub>
        </NoteSection>
      </Container>
    </Page>
  );
}
